import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { miabApi } from '../api/miab';
import { useAuth } from '../hooks/useAuth';

/**
 * Header nudge pointing at the open MIAB survey.
 *
 * Franchisees see a "Survey open" call to action while a window is live and
 * nothing the rest of the time — the announcement email is still the main
 * entry point, this just means they can find it again without digging the
 * email back up. Admins get a permanent link to the management view.
 */
export default function MiabSurveyBadge() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // This runs on every page for every user, so it must never be able to
  // degrade the rest of the dashboard: no retries, and any error just hides
  // the badge (`data` stays undefined) rather than surfacing.
  const { data } = useQuery({
    queryKey: ['miab', 'active'],
    queryFn: miabApi.active,
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isAdmin) {
    return (
      <Link to="/miab/admin" className="miab-badge" title="Marketing in a Box surveys">
        MIAB
        {data && <span className="miab-badge-dot" aria-label="a survey is open" />}
      </Link>
    );
  }

  // Franchisees (and FBCs, who may look but not answer) only see it when open.
  if (!data) return null;

  return (
    <Link
      to={`/miab/${data.survey.slug}`}
      className="miab-badge is-open"
      title={`${data.survey.title} — open now`}
    >
      Survey open
    </Link>
  );
}
